import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useEffect, useMemo, useState } from "react";

import {
  filterTemplates,
  getTemplateCategories,
  getTemplatesByKind,
  type DocumentTemplate,
  type TemplateCategory,
  type TemplateDocumentKind,
} from "../data/templates";
import { TemplateCard } from "./template-card";

type TemplatePickerDialogProps = {
  open: boolean;
  kind: TemplateDocumentKind;
  onOpenChange: (open: boolean) => void;
  onUseTemplate: (template: DocumentTemplate) => void;
};

const getDialogContent = (kind: TemplateDocumentKind) => {
  if (kind === "resume") {
    return {
      title: "Choisissez un modèle de CV",
      description: "Sélectionnez le modèle qui correspond à votre profil.",
    };
  }

  return {
    title: "Choisissez un modèle de lettre",
    description: "Sélectionnez une mise en page professionnelle.",
  };
};

export const TemplatePickerDialog = ({
  open,
  kind,
  onOpenChange,
  onUseTemplate,
}: TemplatePickerDialogProps) => {
  const [selectedCategory, setSelectedCategory] = useState<TemplateCategory>("all");

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const dialogContent = getDialogContent(kind);

  const templates = useMemo(() => getTemplatesByKind(kind), [kind]);

  const categories = useMemo(() => getTemplateCategories(kind), [kind]);

  const visibleTemplates = useMemo(
    () => filterTemplates(templates, selectedCategory),
    [templates, selectedCategory],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedCategory("all");
    setSelectedTemplateId(null);
  }, [open, kind]);

  const handleUseTemplate = (template: DocumentTemplate) => {
    setSelectedTemplateId(template.id);
    onUseTemplate(template);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          /*
           * Les deux propriétés suivantes empêchent
           * tout coin rectangulaire de dépasser.
           */
          "overflow-hidden rounded-[1.5rem]",
          /*
           * Mobile : fenêtre compacte avec une marge
           * autour de l’écran.
           */
          "h-[min(88dvh,720px)]",
          "w-[calc(100vw-1rem)]",
          "max-w-none",
          "grid grid-rows-[auto_minmax(0,1fr)]",
          "gap-0 border border-slate-200",
          "bg-white p-0 shadow-2xl",
          /*
           * Tablette et desktop.
           */
          "sm:h-[min(86dvh,780px)]",
          "sm:w-[calc(100vw-3rem)]",
          "sm:max-w-5xl",
          "sm:rounded-[1.75rem]",
        )}
      >
        <DialogHeader
          className={cn(
            "space-y-0 border-b border-slate-200",
            "bg-white px-4 pb-3 pr-12 pt-4",
            "sm:px-6 sm:pb-4 sm:pr-14 sm:pt-5",
          )}
        >
          <DialogTitle className="text-base font-bold leading-tight text-indigo-950 sm:text-xl">
            {dialogContent.title}
          </DialogTitle>

          <DialogDescription className="mt-1 text-[11px] leading-4 text-slate-600 sm:text-[13px] sm:leading-5">
            {dialogContent.description}
          </DialogDescription>

          {kind === "resume" && (
            <div
              role="tablist"
              aria-label="Catégories de modèles"
              className={cn(
                "mt-3 flex gap-1.5 overflow-x-auto pb-1",
                "[scrollbar-width:none]",
                "[&::-webkit-scrollbar]:hidden",
                "sm:mt-4 sm:gap-2",
              )}
            >
              {categories.map((category) => {
                const isSelected = selectedCategory === category.value;

                return (
                  <button
                    key={category.value}
                    type="button"
                    role="tab"
                    aria-selected={isSelected}
                    onClick={() => {
                      setSelectedCategory(category.value);
                    }}
                    className={cn(
                      "min-h-8 shrink-0 rounded-lg border",
                      "px-2.5 text-[10px] font-semibold",
                      "transition-colors",
                      "focus-visible:outline-none",
                      "focus-visible:ring-2",
                      "focus-visible:ring-indigo-500",
                      "sm:min-h-9 sm:px-4 sm:text-xs",
                      isSelected
                        ? "border-indigo-600 bg-indigo-600 text-white"
                        : "border-slate-200 bg-white text-indigo-950 hover:border-indigo-300 hover:bg-indigo-50",
                    )}
                  >
                    {category.label}
                  </button>
                );
              })}
            </div>
          )}
        </DialogHeader>

        <div
          className={cn(
            "min-h-0 overflow-y-auto overscroll-contain",
            "bg-slate-50/70 px-2.5 py-3",
            "sm:px-5 sm:py-5",
          )}
        >
          {visibleTemplates.length > 0 ? (
            <div
              className={cn(
                /*
                 * Deux modèles par ligne sur mobile.
                 */
                "grid grid-cols-2 gap-2.5",
                /*
                 * Trois modèles à partir de la tablette.
                 */
                "sm:gap-4",
                "md:grid-cols-3",
              )}
            >
              {visibleTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  selected={template.id === selectedTemplateId}
                  onSelect={handleUseTemplate}
                />
              ))}
            </div>
          ) : (
            <div className="grid min-h-48 place-items-center text-center">
              <div>
                <p className="text-xs font-semibold text-indigo-950 sm:text-sm">
                  Aucun modèle disponible
                </p>

                <p className="mt-1 text-[11px] text-slate-500 sm:text-xs">
                  Essayez une autre catégorie.
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
